import { useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { ElementRenderer } from "./element-renderer"
import { DropdownMenu } from "../components/ui/dropdown-menu"

/**
 * DropdownMenuRenderer — base-ui DropdownMenu.Root provider.
 *
 * Children should include dropdown-menu-trigger (and its inner icon/button)
 * + dropdown-menu-content (with dropdown-menu-item children).
 *
 * Open state options:
 *  - Uncontrolled: omit `bind` / `open` / `defaultOpen` → Root manages internally
 *  - Bound: element.bind = "#{ui.menuOpen}" → state-controlled via ActionFlow
 */
export function DropdownMenuRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults: DropdownMenu root is a base-ui provider that
  // doesn't render a DOM element of its own, so we surface the atom
  // contract on a thin wrapping span. The portal'd trigger/content inside
  // continue to render normally.
  const atomDefaults = useAtomDefaults("dropdown")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const [bound, setBound] = useBoundValue<boolean>(
    element.bind,
    resolveSymbol,
    (element as any).defaultOpen === true,
    Boolean,
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (element.bind !== undefined) setBound(open)
      dispatchEvent(element.id, "openChange", { open })
    },
    [element.id, element.bind, setBound, dispatchEvent],
  )

  const rootProps: Record<string, unknown> =
    element.bind !== undefined
      ? { open: bound, onOpenChange: handleOpenChange }
      : (element as any).defaultOpen !== undefined
        ? { defaultOpen: (element as any).defaultOpen === true, onOpenChange: handleOpenChange }
        : { onOpenChange: handleOpenChange }

  return (
    <span
      data-atom-type="dropdown"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-testid={`dropdown-${element.id}`}
    >
      <DropdownMenu {...rootProps}>
        {element.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </DropdownMenu>
    </span>
  )
}
